import { useEffect, useRef, useState } from "react";
import { formatUsdcRaw } from "../../lib/format";
import { parseUsdcAmount, rawUsdcToInputString } from "./blendedApy";

/**
 * Inline composer that drops under the position row when the user
 * clicks Withdraw. Accepts a USDC amount in whole units (with cents),
 * validates against the position's value, and stages the intent.
 */
export function WithdrawComposer({
	maxValueUsdcRaw,
	onSubmit,
	onCancel,
}: {
	maxValueUsdcRaw: string;
	onSubmit: (rawAmount: string) => void;
	onCancel: () => void;
}) {
	const max = BigInt(maxValueUsdcRaw);
	// `rawUsdcToInputString` is the lossless inverse of
	// `parseUsdcAmount` — clicking "max" populates a value that parses
	// back to exactly `max`, so the validator never trips with
	// "exceeds available balance" on the user's own balance. The
	// label below uses `formatUsdcRaw` which truncates to $X.XX for
	// readability; that's expected to differ from `maxDisplay` for
	// balances with sub-cent precision.
	const maxDisplay = rawUsdcToInputString(max);
	const [amount, setAmount] = useState(maxDisplay);
	const inputRef = useRef<HTMLInputElement>(null);

	// The composer opens via an explicit "Withdraw" click on the row,
	// so focusing the amount field is the user's expected next step.
	// Effect-based focus (rather than `autoFocus`) is the a11y-friendly
	// path — it only runs in response to mount, not on every render.
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const parsed = parseUsdcAmount(amount);
	const exceeds = parsed != null && parsed > max;
	const tooSmall = parsed != null && parsed <= 0n;
	const valid = parsed != null && !exceeds && !tooSmall;

	const submit = () => {
		if (!parsed || !valid) return;
		onSubmit(parsed.toString());
	};

	return (
		<tr className="bg-slate-950/60">
			<td colSpan={6} className="px-3 py-3">
				<div className="flex flex-wrap items-center gap-2 text-xs">
					<span className="text-slate-400">Amount</span>
					<div className="flex items-center overflow-hidden rounded-md border border-slate-800 bg-slate-950">
						<input
							ref={inputRef}
							type="text"
							inputMode="decimal"
							className="w-28 bg-transparent px-2 py-1 text-right font-mono text-slate-100 outline-none"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							placeholder="0.00"
						/>
						<span className="border-l border-slate-800 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500">
							USDC
						</span>
					</div>
					<button
						type="button"
						onClick={() => setAmount(maxDisplay)}
						className="rounded-md border border-slate-800 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-300 transition hover:border-emerald-500 hover:text-emerald-300"
					>
						max
					</button>
					<span className="text-[11px] text-slate-500">
						of {formatUsdcRaw(maxValueUsdcRaw)}
					</span>
					{exceeds && (
						<span className="text-[11px] text-rose-300">
							exceeds available balance
						</span>
					)}
					{tooSmall && parsed != null && (
						<span className="text-[11px] text-rose-300">must be &gt; 0</span>
					)}
					<div className="ml-auto flex gap-2">
						<button
							type="button"
							onClick={onCancel}
							className="rounded-md px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 transition hover:text-slate-200"
						>
							cancel
						</button>
						<button
							type="button"
							onClick={submit}
							disabled={!valid}
							className="rounded-md bg-rose-500 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-950 transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Add to plan
						</button>
					</div>
				</div>
			</td>
		</tr>
	);
}
